import React, { useContext, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, AuthContext } from "./context/AuthContext";
import {
  AdminAuthProvider,
  AdminAuthContext,
} from "./context/AdminAuthContext";
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import Layout from "./layout/Layout";
import Home from "./pages/Home";
import LearnMore from "./pages/LearnMore";
import RegisterPage from "./pages/RegisterPage";
import LoginPage from "./pages/LoginPage";
import ProfilePage from "./pages/ProfilePage";
import DjsPage from "./pages/DjsPage";
import DJPublicPage from "./pages/DJPublicPage";
import RequestPage from "./pages/RequestPage";
import DJQrPage from "./pages/DJQrPage";
import VerifyPage from "./pages/VerifyPage";
import StripeCallbackPage from "./pages/StripeCallbackPage";
import SuccessPage from "./pages/SuccessPage";
import RequestsPage from "./pages/RequestsPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import NotFoundPage from "./pages/NotFoundPage";
import Loader from "./components/Loader";
import { RequireAuth, RequireNoAuth } from "./components/RouteGuards";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import { RequireAdminAuth } from "./components/RequireAdminAuth";
import FreeSuccessPage from "./pages/FreeSuccessPage";
import TermsOfService from "./pages/TermsOfService";
// AdminDjPage removed - using AdminDjProfile instead
import PrivacyPolicy from "./pages/PrivacyPolicy";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { GOOGLE_CLIENT_ID } from "./config";
import AdminDjProfile from "./pages/AdminDjProfile";
import EstablishmentPage from "./pages/EstablishmentPage";
import ChatBot from "./components/ChatBot";
import { useLocation } from "react-router-dom";

const ChatBotConditional: React.FC = () => {
  const location = useLocation();
  const path = location.pathname;
  const hideOn = ["/dj/", "/success", "/free-success", "/venue/"];
  if (hideOn.some((p) => path.startsWith(p))) return null;
  return <ChatBot />;
};

const AppContent: React.FC = () => {
  const { loading } = useContext(AuthContext);
  const { loading: adminLoading, admin } = useContext(AdminAuthContext);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      StatusBar.setOverlaysWebView({ overlay: false });
      StatusBar.setStyle({ style: Style.Dark });
    }
  }, []);

  if (loading || adminLoading) return <Loader />;

  return (
    <>
      <Routes>
        <Route
          path="/admin/login"
          element={
            !admin ? <AdminLogin /> : <Navigate to="/admin/dashboard" replace />
          }
        />
        <Route
          path="/admin/dashboard/*"
          element={
            <RequireAdminAuth>
              <AdminDashboard />
            </RequireAdminAuth>
          }
        />
        <Route
          path="/admin/dj/:id"
          element={
            <RequireAdminAuth>
              <AdminDjProfile />
            </RequireAdminAuth>
          }
        />
        <Route path="/venue/:slug" element={<EstablishmentPage />} />
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="learn-more" element={<LearnMore />} />
          <Route path="dj/:slug" element={<DJPublicPage />} />
          <Route path="dj/:slug/request" element={<RequestPage />} />
          <Route path="success" element={<SuccessPage />} />
          <Route path="/free-success" element={<FreeSuccessPage />} />
          <Route path="privacy" element={<PrivacyPolicy />} />
          <Route path="terms" element={<TermsOfService />} />
          <Route element={<RequireNoAuth />}>
            <Route path="register" element={<RegisterPage />} />
            <Route path="register/stripe/callback" element={<StripeCallbackPage />} />
            <Route path="login" element={<LoginPage />} />
            <Route path="forgot-password" element={<ForgotPasswordPage />} />
            <Route path="reset-password/:token" element={<ResetPasswordPage />} />
          </Route>
          <Route path=":slug" element={<RequestPage />} />
          <Route element={<RequireAuth />}>
            <Route path="profile" element={<ProfilePage />} />
            <Route path="requests" element={<RequestsPage />} />
            <Route path="qr/:slug" element={<DJQrPage />} />
          </Route>
        </Route>
        <Route path="verify/:token" element={<VerifyPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <ChatBotConditional />
    </>
  );
};

export default function App() {
  if (!GOOGLE_CLIENT_ID) {
    console.error(
      "Google CLIENT_ID is missing. Set REACT_APP_GOOGLE_CLIENT_ID in client/.env for local dev and in Vercel Environment Variables for deployed builds."
    );
  }

  return (
    <BrowserRouter>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID || "missing"}>
        <AuthProvider>
          <AdminAuthProvider>
            <AppContent />
          </AdminAuthProvider>
        </AuthProvider>
      </GoogleOAuthProvider>
    </BrowserRouter>
  );
}
